import { createRef, type FC, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  change,
  type Field,
  Form,
  type FormErrors,
  formValueSelector,
  type InjectedFormProps,
  reduxForm,
} from "redux-form";
import { HStack, InfoToggletip, LoadingSpinner } from "widgets";

import { handleTabInForm, simpleDropdownFieldValidation } from "@/app-utils/form.ts";
import TabFence from "@/ui-modules/core/forms/TabFence.tsx";
import ReactSelectField, { ReactSelectReduxFormField } from "@/ui-modules/core/redux-form/ReactSelectField.tsx";
import P2ParamsConfigurator from "@/ui-modules/playbooks/editor/P2ParamsConfigurator.tsx";

import { UiContextEnum, WorkflowParameterTypeEnum } from "../../../graphql.js";
import {
  type ActivityInstanceExtendedParameterWithActivity,
  fetchP2Workflow,
  type IPlaybookConfigState,
  savePlaybook,
  updatePlaybookConfigState,
} from "../../../redux/modules/playbook-admin.js";
import { type IAppState } from "../../../redux/reducer.js";
import { type ReduxDispatch } from "../../../redux/redux-utils.js";
import { PLAYBOOK_TYPE } from "../../../settings/settings.js";
import PlaybookWizardStep, {
  type IPlaybookWizardStepInjectedProps,
  type IPlaybookWizardStepProvidedProps,
} from "../PlaybookWizardStep.js";

type PlaybookConfigP2WorkflowInputScreenProps = IPlaybookWizardStepProvidedProps;

enum PlaybookConfigP2WorkflowInputScreenFormNames {
  FORM_NAME = "p2WorkflowInput",
  GENOME_FIELD = "PLAYBOOK_CREATION_FORM_FIELD_GENOME",
  REGION_FIELD = "PLAYBOOK_CREATION_FORM_FIELD_REGION",
}

const PlaybookConfigP2WorkflowInputForm: FC<PlaybookConfigP2WorkflowInputScreenProps & InjectedFormProps<any, any>> = (
  props: PlaybookConfigP2WorkflowInputScreenProps & IPlaybookWizardStepInjectedProps & InjectedFormProps<any, any>,
) => {
  const dispatch = useDispatch<ReduxDispatch>();
  const state = useSelector<IAppState, IAppState>((state) => state);
  const configState = useSelector<IAppState, Partial<IPlaybookConfigState>>(
    (state) => state.playbookAdminState.playbookConfigState,
  );
  // we can retrieve the following info from the config state because it was set on the previous screen
  const genomeViewerContextSelected = !!configState.uiContexts?.find((c) => c === UiContextEnum.GenomeViewer);

  const [isSavingPlaybook, setSavingPlaybook] = useState<boolean>(false);
  const [isLoadingData, setLoadingData] = useState<boolean>(true);

  const configurableParameters = configState.p2Params;

  const setConfigurableParameters = (p2Params: ActivityInstanceExtendedParameterWithActivity[]) => {
    dispatch(updatePlaybookConfigState({ p2Params: p2Params }));
  };

  const valueSelector = formValueSelector(PlaybookConfigP2WorkflowInputScreenFormNames.FORM_NAME);
  const mappedGenome: ActivityInstanceExtendedParameterWithActivity = valueSelector(
    state,
    PlaybookConfigP2WorkflowInputScreenFormNames.GENOME_FIELD,
  );
  const mappedRegion: ActivityInstanceExtendedParameterWithActivity = valueSelector(
    state,
    PlaybookConfigP2WorkflowInputScreenFormNames.REGION_FIELD,
  );

  function handleSubmitData(): Promise<any> {
    const playbookCreationState: Partial<IPlaybookConfigState> = {
      p2ContextParams: { genomeParam: mappedGenome, regionParam: mappedRegion },
      p2Params: configurableParameters,
    };

    dispatch(updatePlaybookConfigState(playbookCreationState));

    if (isLastScreen) {
      props.updateWizardMessage?.(null);
      setSavingPlaybook(true);
      return dispatch(savePlaybook())
        .then(() => {
          setSavingPlaybook(false);
          return props.proceedWithNextStep();
        })
        .catch((error: Error) => {
          props.updateWizardMessage?.(error.message);
          setSavingPlaybook(false);
        });
    } else {
      return props.proceedWithNextStep();
    }
  }

  useEffect(() => {
    setLoadingData(true);
    dispatch(fetchP2Workflow(configState.workflowId as number))
      .then((_P2Workflow) => {
        setLoadingData(false);
      })
      .catch((error: Error) => {
        props.updateWizardMessage(error.message);
        setLoadingData(false);
      });
  }, []);

  useEffect(() => {
    const workflowParameters = configState.p2Params;
    const configStateGenomeParam = configState.p2ContextParams?.genomeParam;
    const configStateRegionParam = configState.p2ContextParams?.regionParam;

    if (configStateGenomeParam) {
      const genomeParam = workflowParameters?.find(
        (c) =>
          c.activity.identifier === configStateGenomeParam?.activity.identifier &&
          c.parameterDefinition.id === configStateGenomeParam?.parameterDefinition.id,
      );
      dispatch(
        change(
          PlaybookConfigP2WorkflowInputScreenFormNames.FORM_NAME,
          PlaybookConfigP2WorkflowInputScreenFormNames.GENOME_FIELD,
          genomeParam,
        ),
      );
    }
    if (configStateRegionParam) {
      const regionParam = workflowParameters?.find(
        (c) =>
          c.activity.identifier === configStateRegionParam?.activity.identifier &&
          c.parameterDefinition.id === configStateRegionParam?.parameterDefinition.id,
      );
      dispatch(
        change(
          PlaybookConfigP2WorkflowInputScreenFormNames.FORM_NAME,
          PlaybookConfigP2WorkflowInputScreenFormNames.REGION_FIELD,
          regionParam,
        ),
      );
    }
  }, [configState.p2Params]);

  const isLastScreen: boolean = configState.playbookType === PLAYBOOK_TYPE.PROCESSOR; // ony case right now

  const lastTabRef = createRef<Field>();
  const firstTabRef = createRef<Field>();

  return (
    <PlaybookWizardStep
      {...props}
      playbookPage={{
        ...props.playbookPage,
        description: getPageDescription(genomeViewerContextSelected),
      }}
      wizardFooterData={{
        nextButtonProps: {
          label: isLastScreen ? "Save Playbook" : "Next",
          isDisabled: !!props.wizardErrorMessage || (isLastScreen ? false : false) /* this.props.isLoadingSampleType */,
          isLoading: isLoadingData || (isLastScreen ? isSavingPlaybook : false),
          onPress: props.handleSubmit(handleSubmitData),
        },
      }}
    >
      {isLoadingData ? (
        <LoadingSpinner />
      ) : (
        <>
          {genomeViewerContextSelected && (
            // reduxForm handleSubmit()?
            <Form className="form-horizontal" onSubmit={props.handleSubmit(handleSubmitData)}>
              <div onKeyDownCapture={handleTabInForm}>
                <TabFence targetRef={lastTabRef} />
                <ReactSelectReduxFormField
                  key={PlaybookConfigP2WorkflowInputScreenFormNames.GENOME_FIELD}
                  name={PlaybookConfigP2WorkflowInputScreenFormNames.GENOME_FIELD}
                  label={
                    <HStack>
                      Select a genome parameter
                      <InfoToggletip
                        content={`This is the parameter that will accept the genome from the Genome Viewer.`}
                      />
                    </HStack>
                  }
                  horizontalLayout={true}
                  isClearable={true}
                  isSearchable={true}
                  getOptionValue={(option: ActivityInstanceExtendedParameterWithActivity) => getParameterId(option)}
                  getOptionLabel={(option: ActivityInstanceExtendedParameterWithActivity) =>
                    `${option.parameterDefinition.label} (${option.activity.label})`
                  }
                  component={ReactSelectField}
                  isDisabled={false}
                  isLoading={false}
                  options={
                    configurableParameters?.filter(
                      (p) => p.staged && p.parameterDefinition.type === WorkflowParameterTypeEnum.Genome,
                    ) ?? []
                  }
                  validate={simpleDropdownFieldValidation}
                />
                <ReactSelectReduxFormField
                  key={PlaybookConfigP2WorkflowInputScreenFormNames.REGION_FIELD}
                  name={PlaybookConfigP2WorkflowInputScreenFormNames.REGION_FIELD}
                  label={
                    <HStack>
                      Select a region parameter
                      <InfoToggletip
                        content={`This is the parameter that will accept the genomic region from the Genome Viewer.`}
                      />
                    </HStack>
                  }
                  horizontalLayout={true}
                  isClearable={true}
                  isSearchable={true}
                  getOptionValue={(option: ActivityInstanceExtendedParameterWithActivity) => getParameterId(option)}
                  getOptionLabel={(option: ActivityInstanceExtendedParameterWithActivity) =>
                    `${option.parameterDefinition.label} (${option.activity.label})`
                  }
                  component={ReactSelectField}
                  isDisabled={false}
                  isLoading={false}
                  options={
                    configurableParameters?.filter(
                      (p) => p.staged && p.parameterDefinition.type === WorkflowParameterTypeEnum.GenomicRegion,
                    ) ?? []
                  }
                  validate={simpleDropdownFieldValidation}
                />
                <TabFence targetRef={firstTabRef} />
              </div>
            </Form>
          )}
          <P2ParamsConfigurator
            lockedVisibilityParams={genomeViewerContextSelected ? [mappedGenome, mappedRegion] : []}
            p2ParamConfigurations={configurableParameters ?? []}
            setP2ParamConfigurations={setConfigurableParameters}
          />
        </>
      )}
    </PlaybookWizardStep>
  );
};

const getPageDescription = (genomeViewerContextSelected: boolean) => {
  return `Configure ${genomeViewerContextSelected ? "the input parameters specific for Genome Viewer context and" : ""} the workflow parameters exposed by the workflow.`;
};

const getParameterId = (option: ActivityInstanceExtendedParameterWithActivity) => {
  return `${option.activity.identifier}-${option.parameterDefinition.id}`;
};

const validate = (values, _props: PlaybookConfigP2WorkflowInputScreenProps): FormErrors<unknown> => {
  return {};
};

const PlaybookConfigP2WorkflowInputScreen = reduxForm<unknown, PlaybookConfigP2WorkflowInputScreenProps>({
  form: PlaybookConfigP2WorkflowInputScreenFormNames.FORM_NAME,
  validate,
})(PlaybookConfigP2WorkflowInputForm);

export default PlaybookConfigP2WorkflowInputScreen;
